import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

import { performWhepNegotiation } from "./client";
import type { WHEPConfig, WHEPState, WHEPStreamState, WHEPStore } from "./types";
import type { CCTVInfo, StreamStatus } from "../types";

const BASE_RECONNECT_DELAY = 1000;
const MAX_RECONNECT_DELAY = 30000;

/**
 * 자동 재시도 횟수. 이 횟수를 넘기면 `failed` 로 떨어뜨리고 사람에게 넘긴다.
 *
 * 무한 재시도는 두 가지를 망친다 — 죽은 미디어 서버를 계속 두드리고, 화면에는
 * "연결 중" 스피너만 남아 사용자가 고장을 알아채지 못한다. 짧게 세 번(1s·2s·4s)
 * 시도하면 순간적인 끊김은 사용자 모르게 복구되고, 서버가 정말 죽었을 때만
 * 재연결 버튼이 뜬다.
 */
const MAX_RETRIES = 3;

/**
 * ICE 가 `disconnected` 로 떨어졌을 때 재협상 전에 두는 유예.
 *
 * `disconnected` 는 경로가 잠깐 막힌 상태라 스스로 `connected` 로 돌아오는 일이
 * 흔하다. 즉시 재협상하면 살아 있는 연결을 우리 손으로 끊는 셈이 된다.
 * `failed` 는 회복 가능성이 없는 종착 상태라 유예 없이 재시도한다.
 */
const DISCONNECT_GRACE_MS = 3000;

const DEFAULT_ICE_SERVERS: RTCIceServer[] = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
];

const DEFAULT_CONFIG: Required<WHEPConfig> = {
  iceServers: DEFAULT_ICE_SERVERS,
  autoReconnect: true,
  reconnectDelay: BASE_RECONNECT_DELAY,
  maxReconnectDelay: MAX_RECONNECT_DELAY,
};

const createStreamState = (streamUrl: string): WHEPStreamState => ({
  streamUrl,
  status: "idle",
  pc: null,
  remoteStream: null,
  error: null,
  retryCount: 0,
  retryTimer: null,
  abortController: null,
});

function cleanupStreamState(stream: WHEPStreamState) {
  if (stream.retryTimer) {
    clearTimeout(stream.retryTimer);
  }
  if (stream.abortController) {
    stream.abortController.abort();
  }
  if (stream.pc) {
    stream.pc.close();
  }
  if (stream.remoteStream) {
    stream.remoteStream.getTracks().forEach((t) => t.stop());
  }
}

type SetState = (partial: Partial<WHEPState> | ((state: WHEPState) => Partial<WHEPState>)) => void;
type GetState = () => WHEPStore;

/** 한 스트림의 상태만 갈아끼운다. 이미 사라진 스트림이면 아무 일도 하지 않는다. */
function patchStream(set: SetState, streamUrl: string, patch: Partial<WHEPStreamState>) {
  set((state) => {
    const streams = new Map(state.streams);
    const stream = streams.get(streamUrl);
    if (!stream) return {};
    streams.set(streamUrl, { ...stream, ...patch });
    return { streams };
  });
}

export const useWHEPStore = create<WHEPStore>()(
  subscribeWithSelector((set, get) => ({
    initialized: false,
    config: DEFAULT_CONFIG,
    cctvList: [],
    cctvLoading: false,
    streams: new Map(),

    setConfig: (config: Partial<WHEPConfig>) => {
      set({ config: { ...get().config, ...config } });
    },

    initialize: () => {
      set({ initialized: true });
    },

    cleanup: () => {
      const { streams } = get();

      streams.forEach((stream) => {
        cleanupStreamState(stream);
      });

      set({
        initialized: false,
        streams: new Map(),
      });
    },

    setCCTVList: (list: CCTVInfo[]) => {
      set({ cctvList: list });
    },

    /**
     * 바깥에서 부르는 연결 진입점 — 마운트와 "재연결" 버튼이 쓴다.
     *
     * 이미 붙어 있거나 붙는 중이면 아무 일도 하지 않는다. 재시도 카운터는 여기서
     * 0 으로 되돌린다 — 사람이 다시 누른 것은 새로운 시도지, 앞선 실패의 연장이 아니다.
     */
    connectStream: async (streamUrl: string) => {
      const existing = get().streams.get(streamUrl);

      if (existing && (existing.status === "connected" || existing.status === "connecting")) {
        return;
      }

      await openConnection(streamUrl, 0, get, set);
    },

    disconnectStream: (streamUrl: string) => {
      const { streams } = get();
      const stream = streams.get(streamUrl);

      if (!stream) return;

      cleanupStreamState(stream);

      const newStreams = new Map(streams);
      newStreams.delete(streamUrl);
      set({ streams: newStreams });
    },
  }))
);

/**
 * 실제 연결을 여는 곳. 상태 가드가 없다 — 가드는 부르는 쪽이 판단한다.
 *
 * `connectStream` 은 "이미 연결 중이면 그만" 을 보고 들어오고, 재시도 타이머는
 * 자기가 예약해 둔 것이므로 그대로 들어온다. 예전에는 재시도도 `connectStream`
 * 을 거쳤는데, 재시도 예약이 상태를 `connecting` 으로 남겨 두는 바람에 가드에
 * 걸려 되돌아 나갔다 — 재연결이 통째로 no-op 이었다.
 */
async function openConnection(streamUrl: string, retryCount: number, get: GetState, set: SetState) {
  const existing = get().streams.get(streamUrl);
  if (existing) {
    // 예약된 재시도·유예 타이머가 남아 있으면 지운다. 남겨 두면 이 연결이 성공한
    // 뒤에 뒤늦게 발화해 멀쩡한 연결을 다시 뜯는다.
    if (existing.retryTimer) clearTimeout(existing.retryTimer);
    if (existing.pc) existing.pc.close();
    if (existing.abortController) existing.abortController.abort();
  }

  const abortController = new AbortController();
  const streams = new Map(get().streams);
  streams.set(streamUrl, {
    ...createStreamState(streamUrl),
    status: "connecting",
    retryCount,
    abortController,
  });
  set({ streams });

  const pc = new RTCPeerConnection({ iceServers: get().config.iceServers });
  patchStream(set, streamUrl, { pc });

  pc.ontrack = (e) => {
    const remoteStream = e.streams[0];
    if (remoteStream) patchStream(set, streamUrl, { remoteStream });
  };

  pc.oniceconnectionstatechange = () => {
    const iceState = pc.iceConnectionState;

    if (iceState === "connected") {
      const stream = get().streams.get(streamUrl);
      if (stream?.retryTimer) clearTimeout(stream.retryTimer);
      patchStream(set, streamUrl, {
        status: "connected",
        error: null,
        retryCount: 0,
        retryTimer: null,
      });
      return;
    }

    if (iceState === "failed") {
      scheduleRetry(streamUrl, get, set);
      return;
    }

    if (iceState === "disconnected") {
      // 스스로 돌아올 여지를 준다. 유예가 끝나도 그대로면 그때 재협상한다.
      const stream = get().streams.get(streamUrl);
      if (stream?.retryTimer) return; // 이미 유예 중

      const graceTimer = setTimeout(() => {
        patchStream(set, streamUrl, { retryTimer: null });
        if (pc.iceConnectionState === "disconnected" || pc.iceConnectionState === "failed") {
          scheduleRetry(streamUrl, get, set);
        }
      }, DISCONNECT_GRACE_MS);

      patchStream(set, streamUrl, { retryTimer: graceTimer });
    }
  };

  pc.addTransceiver("video", { direction: "recvonly" });
  pc.addTransceiver("audio", { direction: "recvonly" });

  try {
    await performWhepNegotiation(pc, streamUrl, abortController.signal);
  } catch {
    if (abortController.signal.aborted) return;
    scheduleRetry(streamUrl, get, set);
  }
}

/**
 * 다음 재시도를 예약한다. 남은 횟수가 없으면 `failed` 로 떨어뜨린다.
 *
 * `failed` 는 화면에서 재연결 버튼이 뜨는 유일한 상태다(`StreamErrorOverlay`).
 * 여기서 세팅하지 않으면 사용자는 영원히 스피너만 본다.
 */
function scheduleRetry(streamUrl: string, get: GetState, set: SetState) {
  const stream = get().streams.get(streamUrl);
  if (!stream) return;
  if (stream.retryTimer) clearTimeout(stream.retryTimer);

  const { config } = get();
  const nextRetry = stream.retryCount + 1;

  if (nextRetry > MAX_RETRIES) {
    if (stream.pc) stream.pc.close();
    patchStream(set, streamUrl, {
      status: "failed",
      error: `연결하지 못했습니다 (${MAX_RETRIES}회 시도)`,
      retryTimer: null,
    });
    return;
  }

  const delay = Math.min(config.reconnectDelay * 2 ** (nextRetry - 1), config.maxReconnectDelay);
  const retryTimer = setTimeout(() => {
    void openConnection(streamUrl, nextRetry, get, set);
  }, delay);

  patchStream(set, streamUrl, { status: "connecting", retryCount: nextRetry, retryTimer });
}

// Re-export types
export type { WHEPConfig, WHEPStreamState, WHEPState, WHEPStore, StreamStatus };
